import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ArrowLeft,
  CheckCircle2,
  Edit3,
  Link2,
  Loader2,
  Package,
  Plus,
  Power,
  Save,
  Trash2,
  X,
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const emptyForm = {
  id: null,
  name: '',
  slug: '',
  description: '',
  is_active: true,
};

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

function slugify(value) {
  return (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <p className="text-[11px] font-bold uppercase text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-black text-slate-900">{value || 0}</p>
    </div>
  );
}

export default function AdminPartnerIntegrations({ setActiveComponent }) {
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [autoSlug, setAutoSlug] = useState(true);

  const editing = Boolean(form.id);

  const sortedPartners = useMemo(() => {
    return [...partners].sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [partners]);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/api/admin/integrations/partners`, authHeaders());
      setPartners(response.data.partners || []);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar los partners.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const resetForm = () => {
    setForm(emptyForm);
    setAutoSlug(true);
    setError('');
    setMessage('');
  };

  const handleNameChange = (value) => {
    setForm(prev => ({
      ...prev,
      name: value,
      slug: autoSlug && !editing ? slugify(value) : prev.slug,
    }));
  };

  const handleSlugChange = (value) => {
    setAutoSlug(false);
    setForm(prev => ({ ...prev, slug: slugify(value) }));
  };

  const startEdit = (partner) => {
    setForm({
      id: partner.id,
      name: partner.name || '',
      slug: partner.slug || '',
      description: partner.description || '',
      is_active: Boolean(partner.is_active),
    });
    setAutoSlug(false);
    setError('');
    setMessage('');
  };

  const savePartner = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      description: form.description.trim(),
      is_active: form.is_active,
    };

    try {
      if (editing) {
        await axios.patch(`${API}/api/admin/integrations/partners/${form.id}`, payload, authHeaders());
        setMessage('Partner actualizado correctamente.');
      } else {
        await axios.post(`${API}/api/admin/integrations/partners`, payload, authHeaders());
        setMessage('Partner creado correctamente.');
      }
      resetForm();
      await fetchPartners();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar el partner.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (partner) => {
    setError('');
    setMessage('');
    try {
      await axios.patch(`${API}/api/admin/integrations/partners/${partner.id}`, {
        is_active: !partner.is_active,
      }, authHeaders());
      setMessage(partner.is_active ? 'Partner desactivado.' : 'Partner activado.');
      await fetchPartners();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo actualizar el estado.');
    }
  };

  const deletePartner = async (partner) => {
    const action = partner.connection_count > 0 ? 'desactivar' : 'eliminar';
    const confirmed = window.confirm(`Quieres ${action} ${partner.name}?`);
    if (!confirmed) return;

    setError('');
    setMessage('');
    try {
      const response = await axios.delete(`${API}/api/admin/integrations/partners/${partner.id}`, authHeaders());
      if (response.data.deactivated) {
        setMessage('Partner desactivado para conservar el historial.');
      } else {
        setMessage('Partner eliminado correctamente.');
      }
      if (form.id === partner.id) resetForm();
      await fetchPartners();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo eliminar el partner.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => setActiveComponent('OptionsAdmin')}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </button>
          <div className="min-w-0 flex-1 text-center">
            <h1 className="truncate text-lg font-black text-slate-950 sm:text-2xl">
              Partners e Integraciones
            </h1>
            <p className="hidden text-sm text-slate-500 sm:block">
              Administra plataformas externas que pueden conectar tiendas con Fast Max.
            </p>
          </div>
          <button
            type="button"
            onClick={resetForm}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-3 py-2 text-sm font-bold text-white hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
            Nuevo
          </button>
        </div>
      </div>

      <main className="mx-auto grid max-w-7xl gap-6 px-4 py-6 lg:grid-cols-[380px_1fr]">
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-black text-slate-950">
              {editing ? 'Editar partner' : 'Nuevo partner'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Al activarlo, aparecera en Integraciones para usuarios y proveedores.
            </p>
          </div>

          <form onSubmit={savePartner} className="space-y-4">
            <label className="block">
              <span className="mb-1 block text-sm font-bold text-slate-700">Nombre</span>
              <input
                value={form.name}
                onChange={(event) => handleNameChange(event.target.value)}
                placeholder="Ej. Shopify"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/10"
                required
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-bold text-slate-700">Slug</span>
              <input
                value={form.slug}
                onChange={(event) => handleSlugChange(event.target.value)}
                placeholder="shopify"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/10"
                required
              />
              <p className="mt-1 text-xs text-slate-500">
                Se usa en tokens y URLs internas. Usa letras, numeros y guiones.
              </p>
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-bold text-slate-700">Descripcion</span>
              <textarea
                value={form.description}
                onChange={(event) => setForm(prev => ({ ...prev, description: event.target.value }))}
                rows={4}
                placeholder="Describe como se usara esta integracion."
                className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/10"
              />
            </label>

            <label className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <span>
                <span className="block text-sm font-bold text-slate-700">Activo</span>
                <span className="block text-xs text-slate-500">Visible para generar tokens</span>
              </span>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(event) => setForm(prev => ({ ...prev, is_active: event.target.checked }))}
                className="h-5 w-5 accent-brand-primary"
              />
            </label>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
                {error}
              </div>
            )}
            {message && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
                {message}
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {editing ? 'Guardar cambios' : 'Crear partner'}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-600 hover:bg-slate-50"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </form>
        </section>

        <section className="min-w-0">
          {loading ? (
            <div className="flex min-h-[320px] items-center justify-center rounded-xl border border-slate-200 bg-white">
              <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
            </div>
          ) : (
            <div className="grid gap-4">
              {sortedPartners.map(partner => (
                <article key={partner.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <h3 className="text-xl font-black text-slate-950">{partner.name}</h3>
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-black ${
                          partner.is_active
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {partner.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      <code className="rounded bg-slate-100 px-2 py-1 text-xs font-bold text-slate-700">
                        {partner.slug}
                      </code>
                      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">
                        {partner.description || 'Sin descripcion registrada.'}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(partner)}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                      >
                        <Edit3 className="h-4 w-4" />
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleActive(partner)}
                        className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold ${
                          partner.is_active
                            ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                        }`}
                      >
                        <Power className="h-4 w-4" />
                        {partner.is_active ? 'Desactivar' : 'Activar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => deletePartner(partner)}
                        className="inline-flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-bold text-red-700 hover:bg-red-100"
                      >
                        <Trash2 className="h-4 w-4" />
                        {partner.connection_count > 0 ? 'Desactivar' : 'Eliminar'}
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <Stat label="Conexiones" value={partner.connection_count} />
                    <Stat label="Activas" value={partner.active_connection_count} />
                    <Stat label="Productos" value={partner.product_mapping_count} />
                    <Stat label="Entregas" value={partner.delivery_count} />
                  </div>
                </article>
              ))}

              {!sortedPartners.length && (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
                  <Link2 className="mx-auto mb-3 h-8 w-8 text-slate-400" />
                  <p className="text-sm font-bold text-slate-700">No hay partners registrados.</p>
                  <p className="mt-1 text-sm text-slate-500">Crea el primero desde el formulario.</p>
                </div>
              )}
            </div>
          )}

          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">
            <div className="mb-2 flex items-center gap-2 font-black">
              <Package className="h-4 w-4" />
              Como funciona
            </div>
            Un partner activo aparece en el menu Integraciones de usuarios y proveedores. Cuando una plataforma ya tiene
            conexiones o entregas, Fast Max la desactiva en lugar de borrarla para conservar historial, tokens, productos
            vinculados y webhooks enviados.
          </div>
        </section>
      </main>
    </div>
  );
}
